import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import './stylesheets/InProgressInterviewsModal.css';

interface InterviewProgress {
  id: string;
  sessionId: string;
  userId: string;
  specialty: string;
  startDate: Date;
  endDate?: Date;
  totalQuestions: number;
  questionsAnswered: number;
  currentQuestionIndex: number;
  userName?: string;
}

interface InProgressInterviewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  hideTestUsers?: boolean;
}

const InProgressInterviewsModal: React.FC<InProgressInterviewsModalProps> = ({ 
  isOpen, 
  onClose,
  hideTestUsers = true
}) => {
  const [inProgressInterviews, setInProgressInterviews] = useState<InterviewProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '0px'; // Prevent layout shift
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  const fetchInProgressInterviews = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, get all completed interviews from interviewAttempts
      const attemptsRef = collection(db, 'interviewAttempts');
      const attemptsSnapshot = await getDocs(attemptsRef);
      const completedSessionIds = new Set<string>();

      attemptsSnapshot.docs.forEach(doc => {
        const data = doc.data() as any; // Type cast to handle Firestore data
        const sessionId = data.sessionId || data.session_id;
        if (sessionId) {
          completedSessionIds.add(sessionId);
        }
      });

      // Now get all interviewProgress entries
      const interviewProgressRef = collection(db, 'interviewProgress');
      const progressSnapshot = await getDocs(interviewProgressRef);
      
      if (progressSnapshot.empty) {
        setInProgressInterviews([]);
        return;
      }

      const interviews: InterviewProgress[] = [];

      for (const doc of progressSnapshot.docs) {
        const data = doc.data() as any; // Type cast to handle Firestore data
        const sessionId = data.sessionId || doc.id;
        
        // Only include if this session ID is NOT in the completed interviews
        if (!completedSessionIds.has(sessionId)) {
          // Get total questions from the questions array
          const questionsArray = data.questions || [];
          const totalQuestions = questionsArray.length;
          
          // Get current question index (0-based) and current question number (1-based)
          const currentIndex = data.current_index || 0;
          const currentQuestionNumber = currentIndex + 1;
          
          interviews.push({
            id: doc.id,
            sessionId: sessionId,
            userId: data.userId || data.user_id || '',
            specialty: data.specialty || 'Unknown',
            startDate: data.startDate?.toDate ? data.startDate.toDate() : 
                      data.started_at ? new Date(data.started_at) : new Date(),
            endDate: data.endDate?.toDate ? data.endDate.toDate() : undefined,
            totalQuestions: totalQuestions,
            questionsAnswered: currentIndex, // Number of questions completed (0-based index represents completed questions)
            currentQuestionIndex: currentIndex,
          });
        }
      }

      // Fetch user names
      if (interviews.length > 0) {
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        const usersMap = new Map<string, string>();

        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data() as any; // Type cast to handle Firestore data
          const displayName = userData.name || userData.email || 'Unknown User';
          usersMap.set(doc.id, displayName);
        });

        interviews.forEach(interview => {
          interview.userName = usersMap.get(interview.userId) || 'Unknown User';
        });
      }

      // Sort by start date (most recent first)
      interviews.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());

      setInProgressInterviews(interviews);
    } catch (err) {
      console.error('Error fetching in-progress interviews:', err);
      setError('Failed to load in-progress interviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchInProgressInterviews();
    }
  }, [isOpen, hideTestUsers]);

  const filteredInterviews = inProgressInterviews.filter(interview => {
    // Filter out test users if hideTestUsers is enabled
    if (hideTestUsers) {
      const userName = interview.userName;
      if (userName === 'Mock Medic Admin' || userName === 'Test User 3') {
        return false;
      }
    }
    
    // Apply search filter
    const searchMatch = interview.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      interview.sessionId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      interview.specialty.toLowerCase().includes(searchTerm.toLowerCase());
    
    return searchMatch;
  });

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDuration = (startDate: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - startDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    }
    return `${diffMinutes}m`;
  };

  const getProgressPercentage = (answered: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((answered / total) * 100);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">In-Progress Interviews</h2>
          <button className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>
        
        <div className={`modal-body ${isMobile ? 'mobile' : ''}`}>
          {loading ? (
            <div className="loading">Loading in-progress interviews...</div>
          ) : error ? (
            <div className="no-data">Error: {error}</div>
          ) : (
            <>
              <div className={`search-section ${isMobile ? 'mobile' : ''}`}>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by user, session ID, or specialty..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button 
                    className="btn-clear"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </button>
                )}
              </div>

              <div className="interviews-count">
                {filteredInterviews.length} in-progress interview{filteredInterviews.length !== 1 ? 's' : ''}
                {searchTerm && ` (filtered from ${inProgressInterviews.length} total)`}
              </div>

              {filteredInterviews.length === 0 ? (
                <div className="no-data">
                  {searchTerm ? 'No interviews match your search criteria' : 'No in-progress interviews found'}
                </div>
              ) : (
                <div className={`interviews-grid ${isMobile ? 'mobile' : ''}`}>
                  {filteredInterviews.map((interview) => (
                    <div
                      key={interview.id}
                      className={`interview-card ${isMobile ? 'mobile' : ''}`}
                    >
                      <div className={`card-header ${isMobile ? 'mobile' : ''}`}>
                        <div className="interview-meta">
                          <div className="session-id">
                            Session: {interview.sessionId}
                          </div>
                          <div className="specialty-name">
                            {interview.specialty}
                          </div>
                        </div>
                        <div className="status-badge">
                          In Progress
                        </div>
                      </div>

                      <div className="card-body">
                        <div className="info-row">
                          <span className="info-label">User:</span>
                          <span className="info-value">{interview.userName}</span>
                        </div>
                        
                        <div className="info-row">
                          <span className="info-label">Started:</span>
                          <span className="info-value">{formatDate(interview.startDate)}</span>
                        </div>
                        
                        <div className="info-row">
                          <span className="info-label">Duration:</span>
                          <span className="info-value">{getDuration(interview.startDate)}</span>
                        </div>

                        <div className="progress-info">
                          <div className="progress-label">Question Progress</div>
                          <div className="progress-value">
                            {interview.questionsAnswered} of {interview.totalQuestions} questions completed
                            {interview.totalQuestions > 0 && (
                              <span style={{ color: '#64b5f6', marginLeft: '8px' }}>
                                ({getProgressPercentage(interview.questionsAnswered, interview.totalQuestions)}%)
                              </span>
                            )}
                          </div>
                        </div>

                        {interview.currentQuestionIndex !== undefined && interview.totalQuestions > 0 && (
                          <div className="progress-info">
                            <div className="progress-label">Current Question</div>
                            <div className="progress-value">
                              Question #{interview.currentQuestionIndex + 1} of {interview.totalQuestions}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InProgressInterviewsModal;